import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import toast from 'react-hot-toast';

const ReportGenerator = ({ analyticsData, projectId, dateRange }) => {
  const [reportName, setReportName] = useState('');
  const [reportType, setReportType] = useState('executive');
  const [format, setFormat] = useState('pdf');
  const [frequency, setFrequency] = useState('once');
  const [recipients, setRecipients] = useState('');
  const [includeCharts, setIncludeCharts] = useState(true);
  const [includeTables, setIncludeTables] = useState(true);

  const reportTypes = [
    { value: 'executive', label: 'Reporte Ejecutivo', description: 'Resumen de KPIs principales' },
    { value: 'detailed', label: 'Reporte Detallado', description: 'Análisis completo con gráficos' },
    { value: 'advisor', label: 'Rendimiento de Asesores', description: 'Métricas por asesor' },
    { value: 'campaign', label: 'Análisis de Campañas', description: 'ROI y rendimiento de marketing' },
    { value: 'funnel', label: 'Embudo de Conversión', description: 'Análisis de pipeline' }
  ];

  const formats = [
    { value: 'pdf', label: 'PDF', icon: 'FileText' },
    { value: 'excel', label: 'Excel', icon: 'FileSpreadsheet' },
    { value: 'csv', label: 'CSV', icon: 'Table' }
  ];

  const frequencies = [
    { value: 'once', label: 'Una vez' },
    { value: 'daily', label: 'Diario' },
    { value: 'weekly', label: 'Semanal' },
    { value: 'monthly', label: 'Mensual' },
    { value: 'quarterly', label: 'Trimestral' }
  ];

  const handleGenerateReport = () => {
    if (!reportName) {
      toast?.error('Por favor ingresa un nombre para el reporte');
      return;
    }
    toast?.success(`Generando reporte ${reportName}...`);
    // TODO: Generate report
  };

  const handleScheduleReport = () => {
    if (!reportName || !recipients) {
      toast?.error('Por favor completa todos los campos requeridos');
      return;
    }
    toast?.success('Reporte programado exitosamente');
    // TODO: Schedule report
  };

  return (
    <div className="space-y-6">
      {/* Report Configuration */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="FileText" size={20} className="mr-2 text-primary" />
          Configuración del Reporte
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Nombre del Reporte *
            </label>
            <input
              type="text"
              value={reportName}
              onChange={(e) => setReportName(e?.target?.value)}
              placeholder="Ej: Reporte Mensual Enero 2026"
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Tipo de Reporte
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {reportTypes?.map(type => (
                <button
                  key={type?.value}
                  onClick={() => setReportType(type?.value)}
                  className={`p-4 rounded-lg border-2 transition-smooth text-left ${
                    reportType === type?.value
                      ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="font-medium text-foreground mb-1">{type?.label}</div>
                  <div className="text-xs text-muted-foreground">{type?.description}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Formato de Exportación
              </label>
              <div className="flex space-x-2">
                {formats?.map(fmt => (
                  <button
                    key={fmt?.value}
                    onClick={() => setFormat(fmt?.value)}
                    className={`flex-1 px-4 py-2 rounded-lg border-2 transition-smooth flex items-center justify-center ${
                      format === fmt?.value
                        ? 'border-primary bg-primary/10 text-primary' :'border-border text-muted-foreground hover:border-primary/50'
                    }`}
                  >
                    <Icon name={fmt?.icon} size={16} className="mr-2" />
                    {fmt?.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Frecuencia
              </label>
              <select
                value={frequency}
                onChange={(e) => setFrequency(e?.target?.value)}
                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {frequencies?.map(freq => (
                  <option key={freq?.value} value={freq?.value}>
                    {freq?.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {frequency !== 'once' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Destinatarios (separados por coma)
              </label>
              <input
                type="text"
                value={recipients}
                onChange={(e) => setRecipients(e?.target?.value)}
                placeholder="email1@example.com, email2@example.com"
                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          )}

          <div className="flex items-center space-x-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={includeCharts}
                onChange={(e) => setIncludeCharts(e?.target?.checked)}
                className="mr-2 w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-primary"
              />
              <span className="text-sm text-foreground">Incluir gráficos</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={includeTables}
                onChange={(e) => setIncludeTables(e?.target?.checked)}
                className="mr-2 w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-primary"
              />
              <span className="text-sm text-foreground">Incluir tablas</span>
            </label>
          </div>
        </div>
      </div>
      {/* Report Preview */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Eye" size={20} className="mr-2 text-primary" />
          Vista Previa del Reporte
        </h3>
        <div className="bg-muted/30 rounded-lg p-8 border border-border">
          <div className="max-w-3xl mx-auto bg-white p-8 rounded-lg shadow-sm">
            <div className="border-b border-gray-200 pb-4 mb-6">
              <h2 className="text-2xl font-bold text-gray-900">{reportName || 'Nombre del Reporte'}</h2>
              <p className="text-sm text-gray-600 mt-1">
                Período: {dateRange?.startDate} - {dateRange?.endDate}
              </p>
            </div>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Resumen Ejecutivo</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded">
                    <div className="text-sm text-gray-600">Total Leads</div>
                    <div className="text-2xl font-bold text-gray-900">{analyticsData?.totalLeads || 0}</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded">
                    <div className="text-sm text-gray-600">Tasa de Conversión</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {analyticsData?.conversionRate?.toFixed(1) || 0}%
                    </div>
                  </div>
                </div>
              </div>
              {includeCharts && (
                <div className="bg-gray-50 p-4 rounded h-48 flex items-center justify-center text-gray-500">
                  [Gráficos incluidos aquí]
                </div>
              )}
              {includeTables && (
                <div className="bg-gray-50 p-4 rounded h-32 flex items-center justify-center text-gray-500">
                  [Tablas incluidas aquí]
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Actions */}
      <div className="flex justify-end space-x-3">
        {frequency !== 'once' && (
          <button
            onClick={handleScheduleReport}
            className="px-6 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-smooth flex items-center"
          >
            <Icon name="Clock" size={16} className="mr-2" />
            Programar Reporte
          </button>
        )}
        <button
          onClick={handleGenerateReport}
          className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-smooth flex items-center"
        >
          <Icon name="Download" size={16} className="mr-2" />
          Generar Reporte
        </button>
      </div>
    </div>
  );
};

export default ReportGenerator;